import { MixpanelEvents, useAnalytics } from '@/utils/useAnalytics';
import classnames from 'classnames';
import Link from 'next/link';
import React, { ReactNode } from 'react';

import Button from '@material-ui/core/Button';

import styles from './CallToActionBanner.module.scss';
import CallToActionBannerBg from './CallToActionBannerBg';

export type CallToActionBannerProps = {
  heading?: ReactNode;
  body?: ReactNode;
  primaryCtaText?: string;
  primaryCtaHref?: string; // if provided action button will be a link
  onPrimaryCtaClick?: () => null;
  secondaryCtaText?: string;
  secondaryCtaHref?: string; // if provided action button will be a link
  onSecondaryCtaClick?: () => null;
  dataTestId?: string;
  primaryButtonClassName?: string;
  className?: string;
  withLogo?: boolean;
};

export type TrackedCallToActionBannerProps<T extends keyof MixpanelEvents> =
  CallToActionBannerProps & {
    trackingEvent: T;
    trackingDetails: MixpanelEvents[T];
  };

function CallToActionBanner<T extends keyof MixpanelEvents>(
  props: CallToActionBannerProps | TrackedCallToActionBannerProps<T>,
) {
  const {
    heading,
    body,
    primaryCtaText,
    primaryCtaHref,
    onPrimaryCtaClick = () => null,
    secondaryCtaText,
    secondaryCtaHref,
    onSecondaryCtaClick = () => null,
    dataTestId,
    className,
    withLogo = false,
    primaryButtonClassName,
  } = props;
  const isHeadingOnlyWithAction = heading && !body && primaryCtaText;
  const track = useAnalytics();
  const trackCtaClick = (callback: () => null) => () => {
    if ('trackingEvent' in props) {
      track(props.trackingEvent, props.trackingDetails);
    }
    callback();
  };

  const primaryCta = (
    <Button
      variant="contained"
      href={primaryCtaHref}
      onClick={trackCtaClick(onPrimaryCtaClick)}
      className={classnames(styles.primary, primaryButtonClassName)}
      size={'large'}
      data-testid={'cta-banner-primary-button'}
    >
      {primaryCtaText}
    </Button>
  );

  const secondaryCta = (
    <Button
      href={secondaryCtaHref}
      onClick={trackCtaClick(onSecondaryCtaClick)}
      className={styles.secondary}
      size={'large'}
      data-testid={'cta-banner-secondary-button'}
    >
      {secondaryCtaText}
    </Button>
  );

  const wrapWithLink = (content: ReactNode, href: string) => (
    <Link prefetch={false} href={href} as={href} passHref legacyBehavior>
      {content}
    </Link>
  );

  const bodyContent = body && typeof body === 'string' ? <p>{body}</p> : body;

  return (
    <article className={classnames(styles.container, className)} data-testid={dataTestId}>
      <div
        className={classnames(styles.subContainer, isHeadingOnlyWithAction ? styles.twoColumn : '')}
      >
        {heading && (
          <h2 className={styles.heading} data-testid={'cta-banner-heading-text'}>
            {heading}
          </h2>
        )}
        {body && (
          <div className={styles.body} data-testid={'cta-banner-body-text'}>
            {bodyContent}
          </div>
        )}
        <div className={styles.actions}>
          {primaryCtaText && (
            <>{primaryCtaHref ? wrapWithLink(primaryCta, primaryCtaHref) : primaryCta}</>
          )}
          {secondaryCtaText && (
            <>{secondaryCtaHref ? wrapWithLink(secondaryCta, secondaryCtaHref) : secondaryCta}</>
          )}
        </div>
      </div>
      {withLogo && (
        <div aria-hidden={true} className={styles.bgImage}>
          <CallToActionBannerBg />
        </div>
      )}
    </article>
  );
}

export default CallToActionBanner;
